import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { CenteredModal, Header, HeaderFrame, IconFrame, SearchContainer, SearchInput, StyledCheckBox, TextFrame, CheckBoxLineContainer, CheckBoxConatiner, PepoleDisplayContainer, SelectedMembersContainer, SearchListContainer, IndicationLetterContainer, ListItem, PersonDisplayContainer, BlueBubbleWithShortNameContainer, MinusIconContainer, CheckBoxContainer, ModalButtonContainer } from './SharedStyledComponents'
import { Button, Text } from 'ui'
import { BackArrowIcon, SearchIcon, XIcon, MinusIcon, ForwardArrowIcon } from './icons'
import type { User } from 'utils/models'
import { BlockCouponsModalProps, GroupedContacts } from './types'
import { formatPhoneNumber, groupContactsByLetter, handlePersonCarouselScroll, getVisibleContacts } from './function'
import { useCoupons } from '../../../store/CouponsContext'
import { useCouponsAppData } from '../../../store/CouponsAppDataContext'



const Container = styled(CenteredModal)`
  height: 902px;
  
  @media (max-width: 1081px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    transform: none;
    border-radius: 0;
    padding: 16px;
    background: #F6F6F6;
    max-height: 100vh;
    z-index: 9999;
  }
`

const StyledBlockButton = styled(Button)`
  background-color: #007EFF !important;
  box-shadow: 0 !important;
  padding: 16px !important;
  width: 212px !important;
  
  @media (max-width: 1081px) {
    width: 100% !important;
  }
`

const ResponsiveSearchListContainer = styled(SearchListContainer)`
  @media (max-width: 1081px) {
    max-height: none;
    flex: 1;
    padding-bottom: 100px;
  }
`

const MobileWrapper = styled.div`
  @media (max-width: 1081px) {
    display: none;
  }
`

const numberOfPersons = 8

const BlockCouponsModal: React.FC<BlockCouponsModalProps> = ({
    onClose,
    searchQuery,
    setSearchQuery,
    setIsConfirmModalOpen,
    setConfirmModalTitle,
    setConfirmModalSubtitle,
    setConfirmModalConfirmButtonText,
    setConfirmModalCancelButtonText,
    setConfirmModalIsConfettiVisible,
    setConfirmModalIsGiftIconVisible,
    setConfirmModalConfirmButtonBackground,
    setConfirmModalConfirmButtonBorderColor,
    setConfirmModalConfirmButtonTextColor,
    setConfirmModalCancelButtonBackground,
    setConfirmModalCancelButtonBorderColor,
    setConfirmModalCancelButtonTextColor,
    setBlockedMembersCount
}) => {
    const appData = useCouponsAppData()
    const { getBlockedUsers, blockUsers, unblockUsers } = useCoupons()
    const [contacts, setContacts] = useState<User[]>([])
    const [groupedContacts, setGroupedContacts] = useState<GroupedContacts>({})
    const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set())
    const [scrollPosition, setScrollPosition] = useState(0)
    const [isLoadingBlockedUsers, setIsLoadingBlockedUsers] = useState(false)
    const [previouslyBlockedUserIds, setPreviouslyBlockedUserIds] = useState<Set<number>>(new Set())

    useEffect(() => {
        if (appData.orgMembersSorted) {
            setContacts(appData.orgMembersSorted)
        }
    }, [appData.orgMembersSorted])

    // Prefetch blocked users when modal opens and pre-check them
    useEffect(() => {
        const loadBlockedUsers = async () => {
            const currentCommunityId = appData.currentCommunityId
            if (!currentCommunityId) {
                console.warn('⚠️ No current community ID, cannot load blocked users')
                return
            }

            setIsLoadingBlockedUsers(true)
            try {
                const blockedUserIds = await getBlockedUsers(currentCommunityId)
                // Convert string IDs to numbers and set as selected
                const blockedNumbers = new Set<number>()
                blockedUserIds.forEach(id => {
                    const numId = Number(id)
                    if (!isNaN(numId)) {
                        blockedNumbers.add(numId)
                    }
                })
                setSelectedContacts(blockedNumbers)
                setPreviouslyBlockedUserIds(blockedNumbers) // Store for comparison
                console.log(`✅ Pre-loaded ${blockedNumbers.size} blocked users for community ${currentCommunityId}`)
            } catch (error) {
                console.error('❌ Error loading blocked users:', error)
            } finally {
                setIsLoadingBlockedUsers(false)
            }
        }

        loadBlockedUsers()
    }, [getBlockedUsers])

    useEffect(() => {
        const grouped = groupContactsByLetter(contacts, searchQuery)
        setGroupedContacts(grouped)
    }, [contacts, searchQuery])

    const handleContactSelect = (userId: number) => {
        setSelectedContacts(prev => {
            const newSet = new Set(prev)
            if (newSet.has(userId)) {
                newSet.delete(userId)
            } else {
                newSet.add(userId)
            }
            return newSet
        })
    }

    const handleScrollLeft = () => {
        setScrollPosition(prev =>
            handlePersonCarouselScroll('left', prev, selectedContacts.size, numberOfPersons)
        )
    }

    const handleScrollRight = () => {
        setScrollPosition(prev =>
            handlePersonCarouselScroll('right', prev, selectedContacts.size, numberOfPersons)
        )
    }

    const shouldShowArrows = selectedContacts.size > numberOfPersons
    const visibleContacts = getVisibleContacts(selectedContacts, scrollPosition, numberOfPersons)

    const handleBlockAllToggle = () => {
        if (selectedContacts.size === contacts.length) {
            // If all contacts are selected, deselect all
            setSelectedContacts(new Set())
        } else {
            // If not all contacts are selected, select all
            const allContactIds = new Set(contacts.map(contact => contact.user_id))
            setSelectedContacts(allContactIds)
        }
    }

    const isAllBlocked = selectedContacts.size === contacts.length && contacts.length > 0

    const handleConfirmBlock = async () => {
        const currentCommunityId = appData.currentCommunityId
        if (!currentCommunityId) {
            console.warn('⚠️ No current community ID, cannot block/unblock users')
            return
        }

        // Find users to block (newly selected) and unblock (previously blocked but now unselected)
        const newlySelected = Array.from(selectedContacts).filter(id => !previouslyBlockedUserIds.has(id))
        const toUnblock = Array.from(previouslyBlockedUserIds).filter(id => !selectedContacts.has(id))

        // Block newly selected users
        if (newlySelected.length > 0) {
            const selectedUserDetails = contacts
                .filter(contact => newlySelected.includes(contact.user_id))
                .map(contact => ({
                    userId: String(contact.user_id),
                    name: contact.displayName || contact.nick_name || '',
                    phone: contact.phones?.[0] || '',
                    firstName: contact.given_name,
                    lastName: contact.family_name,
                    displayName: contact.displayName,
                    profileName: contact.nick_name,
                }))

            const userIds = newlySelected.map(id => String(id))
            await blockUsers(userIds, selectedUserDetails, currentCommunityId)
        }

        // Unblock users that were previously blocked but are now unselected
        if (toUnblock.length > 0) {
            const userIdsToUnblock = toUnblock.map(id => String(id))
            await unblockUsers(userIdsToUnblock, currentCommunityId)
        }

        // Update previously blocked set for next time
        setPreviouslyBlockedUserIds(new Set(selectedContacts))

        // Pass the count to parent
        setBlockedMembersCount(selectedContacts.size)

        // Set all confirm modal values
        setConfirmModalTitle('בטוח שהעדפת לחסום את חברי הקהילה שבחרת?')
        setConfirmModalSubtitle('שימו לב! החסימה היא פעולה דו כיוונית, חברי הקהילה החסומים לא יוכלו לשתף אתכם הטבות, ואתם לא תוכלו לשתף איתם.')
        setConfirmModalConfirmButtonText('אישור החסימה')
        setConfirmModalCancelButtonText('ביטול')
        setConfirmModalIsConfettiVisible(false)
        setConfirmModalIsGiftIconVisible(false)
        setConfirmModalConfirmButtonBackground('#FF1D51')
        setConfirmModalConfirmButtonBorderColor('#FF1D51')
        setConfirmModalConfirmButtonTextColor('white')
        setConfirmModalCancelButtonBackground('#FFF')
        setConfirmModalCancelButtonBorderColor('#B0B0B0')
        setConfirmModalCancelButtonTextColor('#272727')

        // Close this modal and open confirm modal
        onClose()
        setIsConfirmModalOpen(true)
    }

    return <Container>
        <Header>
            <HeaderFrame>
                <IconFrame>
                    <BackArrowIcon fill='#272727' />
                </IconFrame>
                <TextFrame>
                    <MobileWrapper>
                        <Text size='md' weight='normal' textcolor='#272727'>איזור אישי</Text>
                    </MobileWrapper>
                    <Text size='xxl' weight='bold' textcolor='#272727'>חסימת קבלת הטבות</Text>
                </TextFrame>

            </HeaderFrame>
            <div style={{ cursor: 'pointer' }} onClick={onClose}>
                <XIcon />
            </div>
        </Header>
        <SearchContainer>
            <SearchIcon />
            <SearchInput
                placeholder="כולם פה, את מי לחסום?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />
        </SearchContainer>
        <CheckBoxLineContainer>
            <CheckBoxConatiner>
                <StyledCheckBox
                    type='checkbox'
                    checked={isAllBlocked}
                    onChange={handleBlockAllToggle}
                />
            </CheckBoxConatiner>
            <Text size='md' weight='normal' textcolor='#272727'>אני רוצה לחסום את כולם</Text>
        </CheckBoxLineContainer>
        {selectedContacts.size > 0 && (
            <PepoleDisplayContainer justifyContent={selectedContacts.size > numberOfPersons ? 'center' : 'flex-start'}>
                {shouldShowArrows && (
                    <div style={{ cursor: 'pointer' }} onClick={handleScrollLeft}>
                        <BackArrowIcon fill='#272727' />
                    </div>
                )}

                {visibleContacts.map(userId => {
                    const contact = contacts.find(c => c.user_id === userId)
                    if (!contact) return null
                    return (
                        <PersonDisplayContainer key={userId}>
                            <BlueBubbleWithShortNameContainer>
                                <MinusIconContainer onClick={() => handleContactSelect(userId)}>
                                    <MinusIcon />
                                </MinusIconContainer>
                                <Text size="xxl" weight="bold" style={{ color: '#0692FF' }}>
                                    {contact.given_name?.charAt(0) || 'א'}
                                </Text>
                            </BlueBubbleWithShortNameContainer>
                            <Text size="sm" weight="bold">{contact.given_name}</Text>
                        </PersonDisplayContainer>
                    )
                })}

                {shouldShowArrows && (
                    <div style={{ cursor: 'pointer' }} onClick={handleScrollRight}>
                        <ForwardArrowIcon fill='#272727' />
                    </div>
                )}
            </PepoleDisplayContainer>
        )}
        <SelectedMembersContainer>
            <Text size="sm" weight="normal" style={{ color: '#888888' }}>
                נבחרו {selectedContacts.size} חברי קהילה
            </Text>
        </SelectedMembersContainer>
        <ResponsiveSearchListContainer>
            {Object.entries(groupedContacts)
                .sort(([a], [b]) => a.localeCompare(b, 'he'))
                .map(([letter, contacts]) => (
                    <div key={letter}>
                        <IndicationLetterContainer>
                            <Text size="sm" weight="bold">{letter}</Text>
                        </IndicationLetterContainer>
                        {contacts.map(contact => (
                            <ListItem key={contact.user_id}>
                                <CheckBoxContainer>
                                    <StyledCheckBox
                                        type="checkbox"
                                        checked={selectedContacts.has(contact.user_id)}
                                        onChange={() => handleContactSelect(contact.user_id)}
                                    />
                                </CheckBoxContainer>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Text size="sm" weight="bold">
                                        {contact.given_name} {contact.family_name}
                                    </Text>
                                    <Text size="sm" weight="normal" style={{ color: '#888888', marginLeft: '10px' }}>
                                        {contact.phones?.[0] ? formatPhoneNumber(contact.phones[0]) : 'אין טלפון'}
                                    </Text>
                                </div>
                            </ListItem>
                        ))}
                    </div>
                ))
            }
        </ResponsiveSearchListContainer>
        <ModalButtonContainer isFixed={true}>
            <StyledBlockButton
                variant='contained'
                onClick={handleConfirmBlock}
            >
                <Text size='xxl' weight='bold' textcolor='white'>
                    אישור
                </Text>
            </StyledBlockButton>
        </ModalButtonContainer>
    </Container>
}


export default BlockCouponsModal